import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Check, ChevronRight, Award, Clock, BookOpen, PlayCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * PULA LEARN — course player: sequential modules, per-module quizzes (80% pass),
 * progress tracking. Pre-launch: free preview enrolment; certificates stay locked.
 */

export default function CoursePlayerPage() {
  const { slug } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [active, setActive] = useState(1);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/learn/courses/${slug}/player`);
      setState(data);
      const firstIncomplete = data.modules.find((m) => !m.completed);
      setActive(firstIncomplete ? firstIncomplete.order : data.modules[data.modules.length - 1]?.order || 1);
    } catch (err) {
      if (err?.response?.status === 404) setNotFound(true);
      else if (err?.response?.status === 401) navigate("/login");
      else toast.error("Could not load the course — please refresh.");
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, user, authLoading]);

  const enrol = async () => {
    setBusy(true);
    try {
      await api.post(`/learn/courses/${slug}/enrol`);
      toast.success("You're enrolled — free preview while we await accreditation.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Enrolment failed — please try again.");
    } finally { setBusy(false); }
  };

  const submitQuiz = async (mod) => {
    const quiz = mod.quiz || [];
    const ans = quiz.map((_, i) => answers[i]);
    if (quiz.length && ans.some((a) => a === undefined)) {
      toast.error(`Answer all ${quiz.length} questions.`);
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post(`/learn/courses/${slug}/modules/${mod.order}/complete`, { answers: ans });
      setResult(data);
      if (data.passed) {
        toast.success(`Passed with ${data.score}% — next module unlocked.`);
        await load();
      } else {
        toast.error(`${data.score}% — you need ${data.pass_mark}%. Re-read the module and try again.`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed — please try again.");
    } finally { setBusy(false); }
  };

  if (notFound) return (
    <main className="py-24 text-center" data-testid="course-not-found">
      <p className="text-gray-600 mb-4">Course not found.</p>
      <Link to="/learn/bundles" className="mv-green-text underline">Back to bundles</Link>
    </main>
  );
  if (!state) return <main className="py-24 text-center text-sm text-gray-500" data-testid="course-loading">Loading course…</main>;

  const mod = state.modules.find((m) => m.order === active);
  const doneCount = state.modules.filter((m) => m.completed).length;

  return (
    <main className="bg-white min-h-[70vh]" data-testid="course-player-page">
      <section className="mv-green-bg text-white py-10 md:py-14">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <p className="text-xs uppercase tracking-widest mv-gold-text font-semibold mb-2 flex items-center gap-2">
            <BookOpen size={14} /> {state.course.level}
            {!state.cpd_accredited && <span className="px-2 py-0.5 rounded-full bg-white/15 border border-white/25 text-white">PRE-LAUNCH PREVIEW</span>}
          </p>
          <h1 className="text-2xl md:text-4xl font-bold mb-2">{state.course.title}</h1>
          <p className="text-sm text-white/75 flex items-center gap-4 flex-wrap">
            <span className="flex items-center gap-1"><Clock size={14} /> ~{state.course.duration_min} min</span>
            <span>{doneCount}/{state.modules.length} modules complete</span>
          </p>
        </div>
      </section>

      {!state.enrolled ? (
        <section className="py-16 text-center max-w-xl mx-auto px-4">
          <PlayCircle size={40} className="mx-auto mb-4 mv-green-text" />
          <h2 className="text-xl font-bold mb-2">Start your free preview</h2>
          <p className="text-sm text-gray-600 mb-6">
            {state.cpd_accredited
              ? "Enrol to begin this course."
              : "While we await CPD accreditation, enrolment is a free preview: full course content, quizzes and progress tracking. Accredited certificates unlock at launch."}
          </p>
          <button onClick={enrol} disabled={busy} data-testid="course-enrol-btn"
            className="btn-gold px-6 py-3 rounded-lg font-semibold transition-colors disabled:opacity-60">
            {busy ? "Enrolling…" : "Enrol — free preview"}
          </button>
        </section>
      ) : state.course_completed ? (
        <section className="py-16 text-center max-w-xl mx-auto px-4" data-testid="course-complete-panel">
          <Award size={44} className="mx-auto mb-4 mv-gold-text" />
          <h2 className="text-2xl font-bold mb-3">Course complete — well done.</h2>
          <p className="text-sm text-gray-600 mb-2">
            You've passed all {state.modules.length} modules.
          </p>
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
            {state.cpd_accredited
              ? "Your certificate is ready — visit your learning record to download it."
              : "Your accredited certificate will be issued automatically the day our CPD accreditation is confirmed. Your completion is saved — nothing more to do."}
          </p>
          <Link to="/learn/bundles" className="inline-block mt-6 mv-green-text underline text-sm" data-testid="complete-back-link">Browse more bundles</Link>
        </section>
      ) : (
        <section className="max-w-6xl mx-auto px-4 md:px-8 py-10 grid md:grid-cols-[260px_1fr] gap-8">
          {/* Module sidebar */}
          <nav className="space-y-2" data-testid="module-nav">
            {state.modules.map((m) => (
              <button key={m.order} onClick={() => { if (m.unlocked) { setActive(m.order); setAnswers({}); setResult(null); } }}
                disabled={!m.unlocked} data-testid={`module-tab-${m.order}`}
                className={`w-full text-left px-4 py-3 rounded-lg border text-sm transition-colors flex items-center gap-2 ${
                  active === m.order ? "mv-green-bg text-white border-transparent"
                  : m.unlocked ? "bg-white mv-hairline hover:bg-gray-50"
                  : "bg-gray-50 text-gray-400 cursor-not-allowed border-transparent"}`}>
                {m.completed ? <Check size={15} className="shrink-0" /> : !m.unlocked ? <Lock size={15} className="shrink-0" /> : <ChevronRight size={15} className="shrink-0" />}
                <span className="flex-1">{m.order}. {m.title}</span>
              </button>
            ))}
          </nav>

          {/* Active module */}
          {mod && (
            <article data-testid={`module-content-${mod.order}`}>
              <h2 className="text-xl md:text-2xl font-bold mb-1">Module {mod.order}: {mod.title}</h2>
              <p className="text-xs text-gray-500 mb-6">{mod.summary} · ~{mod.duration_min} min</p>
              <div className="space-y-4 mb-10">
                {mod.content.map((p, i) => (
                  <p key={i} className="text-sm md:text-base text-gray-700 leading-relaxed" data-testid={`module-para-${mod.order}-${i}`}>{p}</p>
                ))}
              </div>

              {mod.completed ? (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-900" data-testid={`module-passed-${mod.order}`}>
                  Module passed{bestScoreText(mod)}. {mod.order < state.modules.length && "The next module is unlocked."}
                </div>
              ) : mod.quiz?.length > 0 && (
                <div className="rounded-xl border mv-hairline p-6" data-testid={`quiz-${mod.order}`}>
                  <h3 className="font-bold mb-4 text-base">Knowledge check — pass at {state.course.pass_mark_percent}% to unlock the next module</h3>
                  <div className="space-y-6">
                    {mod.quiz.map((q, qi) => (
                      <fieldset key={qi} data-testid={`quiz-q-${mod.order}-${qi}`}>
                        <legend className="text-sm font-semibold mb-2">{qi + 1}. {q.q}</legend>
                        <div className="space-y-1.5">
                          {q.options.map((opt, oi) => (
                            <label key={oi} className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                              <input type="radio" name={`q-${mod.order}-${qi}`} className="mt-1"
                                checked={answers[qi] === oi}
                                onChange={() => setAnswers({ ...answers, [qi]: oi })}
                                data-testid={`quiz-q-${mod.order}-${qi}-opt-${oi}`} />
                              <span>{opt}</span>
                            </label>
                          ))}
                        </div>
                      </fieldset>
                    ))}
                  </div>
                  <button onClick={() => submitQuiz(mod)} disabled={busy} data-testid={`quiz-submit-${mod.order}`}
                    className="btn-green mt-6 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-60">
                    {busy ? "Marking…" : "Submit answers"}
                  </button>
                  {result && !result.passed && (
                    <p className="mt-3 text-sm text-red-700" data-testid="quiz-fail-msg">
                      Scored {result.score}% (need {result.pass_mark}%). Re-read the module above and try again — retakes are unlimited.
                    </p>
                  )}
                </div>
              )}
            </article>
          )}
        </section>
      )}
    </main>
  );
}

function bestScoreText(m) {
  return m.best_score != null ? ` (best score ${m.best_score}%)` : "";
}
